import {
  confirmPaymentIntent,
  createElement,
  createPaymentConsent,
  getElement,
  loadAirwallex,
} from 'airwallex-payment-elements';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import {
  CreatePaymentIntentRequest,
  CreatePaymentIntentResponse,
} from '../../../lib/types';
import { countryListAlpha2 } from '../../countries';

interface Props {
  paymentPageId: number;
  onSuccess: (data: unknown) => void;
  onFailure: (data: unknown, orderId: string) => void;
  onProcessing: (data: unknown) => void;
}

enum PaymentState {
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
  PROCESSING = 'processing',
}

const CustomerDetails = ({
  paymentPageId,
  onFailure,
  onProcessing,
  onSuccess,
}: Props) => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const navigate = useNavigate();
  // const [errorMessage, setErrorMessage] = useState('');
  // const [paymentMessage, setPaymentMessage] = useState('');

  const [paymentStatus, setPaymentStatus] = useState<PaymentState>(
    PaymentState.PENDING
  );

  const countries = countryListAlpha2();

  // get the payment page id from the url
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setPaymentStatus(PaymentState.PROCESSING);
    setPaymentError(null);

    try {
      const customerDetails: CreatePaymentIntentRequest = {
        firstname: data.first_name,
        lastname: data.last_name,
        email: data.email,
        countryCode: data.country_code,
        street: data.street,
        postalCode: data.postalCode,
        state: data.state,
        city: data.city,
        website: '',
        phone: data.phone || '',
      };

      // get the payment intent
      const paymentIntent = await axios.post<CreatePaymentIntentResponse>(
        `${process.env.REACT_APP_API_URL}/api/v2/payment-pages/create-payment/${id}`,
        {
          ...customerDetails,
        }
      );

      const element = getElement('cardNumber');

      console.log('paymentIntent', paymentIntent);
      if (paymentDetails.recurring === 'recurring') {
        console.log('calling generatePaymentConsent');
        await generatePaymentConsent(
          paymentIntent.data,
          customerDetails,
          element
        );
      } else {
        console.log('calling processPaymentIntent');
        await processPaymentIntent(
          paymentIntent.data,
          customerDetails,
          element
        );
      }
    } catch (error) {
      // handle error
    }
  };

  const instantiateAirWallex = async () => {
    await loadAirwallex({
      env: 'prod', // Can choose other production environments, 'staging | 'demo' | 'prod'
      origin: window.location.origin, // Setup your event target to receive the browser events message
      fonts: [
        // Customizes the font for the payment elements
        {
          src: 'https://checkout.airwallex.com/fonts/CircularXXWeb/CircularXXWeb-Regular.woff2',
          family: 'AxLLCircular',
          weight: 400,
        },
      ],
      // For more detailed documentation at https://github.com/airwallex/airwallex-payment-demo/tree/master/docs#loadAirwallex
    }).then(() => {
      // STEP #4, 5: Create and mount the individual card elements
      createElement('cardNumber')?.mount('cardNumber'); // This 'cardNumber' id MUST MATCH the id on your cardNumber empty container created in Step 3
      createElement('cvc')?.mount('cvc'); // Same as above
      createElement('expiry')?.mount('expiry'); // Same as above
    });
  };

  const fetchPaymentDetails = async () => {
    const result = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/payment-page/${paymentPageId}`
    );

    return result;
  };

  useEffect(() => {
    console.log(isLoaded);
    if (isLoaded) return;

    const getDetails = async () => {
      await instantiateAirWallex();
      const result = await fetchPaymentDetails();

      setPaymentDetails(result.data);

      console.log('result', result);

      if (result.data.paymentPage.active === false) {
        navigate('/error/404');
      }
    };

    getDetails();

    return () => {
      setIsLoaded(true);
    };
  }, [paymentPageId]);

  const processPaymentIntent = async (
    paymentIntent: CreatePaymentIntentResponse,
    customer: CreatePaymentIntentRequest,
    element: any
  ) => {
    try {
      const result = await confirmPaymentIntent({
        element: element,

        // payment intent id
        id: paymentIntent.intentId,

        // client secret
        client_secret: paymentIntent.clientSecret,

        // customer id
        customer_id: paymentIntent.customerId,

        payment_method_options: {
          card: {
            auto_capture: true,
          },
        },

        // Add other payment confirmation details, see docs here: https://github.com/airwallex/airwallex-payment-demo/tree/master/docs
        payment_method: {
          card: {},
          // billing details - required for risk
          billing: {
            // address - required
            address: {
              // city - required
              city: customer.city,

              // country - required
              postcode: customer.postalCode,

              // state - required
              state: customer.state,

              // street - required
              street: customer.street,

              // country code - required
              country_code: customer.countryCode,
            },

            // first name - required
            first_name: customer.firstname,

            // last name - required
            last_name: customer.lastname,

            // phone number - required
            phone_number: customer.phone,

            // email - required
            email: customer.email,
          },
        },
      });

      setPaymentStatus(PaymentState.SUCCESS);
      setPaymentResult(result);
      setPaymentError(null);
      console.log(result);
    } catch (error) {
      setPaymentStatus(PaymentState.FAILED);
      setPaymentError(error.message ?? JSON.stringify(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate the payment consent
  const generatePaymentConsent = async (
    paymentIntent: CreatePaymentIntentResponse,
    customer: CreatePaymentIntentRequest,
    element: any
  ) => {
    try {
      const result = await createPaymentConsent({
        // element
        element: element,

        // payment intent id
        intent_id: paymentIntent.intentId,

        // client secret
        client_secret: paymentIntent.clientSecret,

        // customer id
        customer_id: paymentIntent.customerId,

        // currency
        currency: paymentIntent.currency,

        // next triggered by
        next_triggered_by: 'merchant',

        // merchant_trigger_reason
        merchant_trigger_reason: 'unscheduled',

        // billing
        billing: {
          address: {
            // city - required
            city: customer.city,

            // postcode - required
            postcode: customer.postalCode,

            // state - required
            state: customer.state,

            // street - required
            street: customer.street,

            // country code - required
            country_code: customer.countryCode,
          },

          // first name - required
          first_name: customer.firstname,

          // last name - required
          last_name: customer.lastname,

          // phone number - required
          phone_number: customer.phone,

          // email - required
          email: customer.email,
        },
      });

      setPaymentStatus(PaymentState.SUCCESS);
      setPaymentResult(result);
      setPaymentError(null);
      console.log(result);
    } catch (error) {
      setPaymentStatus(PaymentState.FAILED);
      setPaymentError(error.message ?? JSON.stringify(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRetryButtonClick = () => {
    reset();
    setPaymentStatus(PaymentState.PENDING);
    setPaymentError(null);
    setPaymentResult(null);
    getElement('cardNumber').clear();
    getElement('cvc').clear();
    getElement('expiry').clear();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {[PaymentState.FAILED, PaymentState.SUCCESS].includes(paymentStatus) && (
        <div>
          <div>
            {paymentStatus === PaymentState.SUCCESS && (
              <div className="my-4 sm:my-0 bg-green-200 rounded-sm border border-green-400">
                <div>
                  <div className="text-green-800 text-center p-4 border-b border-green-400 font-bold">
                    Payment successful!
                  </div>
                  <div className="bg-white p-2 py-8">
                    <div className="m-auto text-sm md:text-md">
                      {' '}
                      <span className="font-bold">Receipt:</span>{' '}
                      {paymentResult && paymentResult.request_id}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            {paymentStatus === PaymentState.FAILED && (
              <div className="my-4 bg-red-200 rounded-sm border border-red-400">
                <div>
                  <div className="text-red-800 text-center p-4 border-b border-red-400  font-bold">
                    Payment failed!
                  </div>
                  <div className="bg-white p-2 py-8">
                    <div className="m-auto text-sm md:text-md">
                      {' '}
                      <div className="text-center mb-4">
                        Unfortunately we could not process your payment.
                      </div>
                      <div className="text-center">
                        <button
                          className="bg-green-600 p-4 rounded text-white font-bold"
                          onClick={onRetryButtonClick}
                        >
                          Retry payment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className={`w-full ${
          [PaymentState.FAILED, PaymentState.SUCCESS].includes(paymentStatus)
            ? 'hidden'
            : ''
        }`}
      >
        <div className="flex flex-col">
          <div className="mb-4 mt-4 md:mt-0">
            <div className="text-sm">Your Details</div>
            <hr />
          </div>
          <div className="flex flex-col md:flex-row mb-4">
            <div className="mb-3 md:mb-0 mr-0 md:mr-4 grow">
              <input
                type="text"
                {...register('first_name', { required: 'Required' })}
                className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
                placeholder="First name"
              />
              {errors.first_name && (
                <span className="text-xs text-red-600">
                  {errors.first_name.message.toString()}
                </span>
              )}
            </div>
            <div>
              <input
                type="text"
                {...register('last_name', { required: 'Required' })}
                className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
                placeholder="Last name"
              />
              {errors.last_name && (
                <span className="text-xs text-red-600">
                  {errors.last_name.message.toString()}
                </span>
              )}
            </div>
          </div>
          <div className="my-6">
            <div className="text-sm">Billing Details</div>
            <hr />
          </div>
          <div className="mb-4">
            <div>
              <input
                type="text"
                {...register('email', {
                  required: 'Required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
                placeholder="Email"
              />
              {errors.email && (
                <span className="text-xs text-red-600">
                  {errors.email.message.toString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col pb-3">
            <input
              type="text"
              className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
              id="street"
              placeholder="Street"
              {...register('street', { required: 'Required' })}
            />
            {errors.street && (
              <span className="text-xs text-red-600">
                {errors.street.message.toString()}
              </span>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <input
              type="text"
              className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
              id="city"
              placeholder="City"
              {...register('city', { required: 'Required' })}
            />
            {errors.city && (
              <span className="text-xs text-red-600">
                {errors.city.message.toString()}
              </span>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <select
              className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
              {...register('country_code', { required: 'Required' })}
            >
              <option value="" className="text-gray-300">
                Select Country
              </option>
              {Object.entries(countries).map(([key, value]) => (
                <option value={key}>{value}</option>
              ))}
            </select>
            {errors.street && (
              <span className="text-xs text-red-600">
                {errors.street.message.toString()}
              </span>
            )}
          </div>
          <div className="flex flex-row">
            <div className="mb-4 grow mr-2">
              <div>
                <input
                  type="text"
                  {...register('state', {
                    // required: 'Required',
                  })}
                  className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
                  placeholder="State (optional)"
                />
                {errors.state && (
                  <span className="text-xs text-red-600">
                    {errors.state.message.toString()}
                  </span>
                )}
              </div>
            </div>
            <div className="mb-4 grow ml-2">
              <div>
                <input
                  type="text"
                  {...register('postalCode', {})}
                  className="border border-gray-300 px-2 py-2 w-full bg-gray-50"
                  placeholder="Postal Code (optional)"
                />
                {errors.postalCode && (
                  <span className="text-xs text-red-600">
                    {errors.postalCode.message.toString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-6">
            <div className="text-sm">Card Details</div>
            <hr />
          </div>
          {/* payment form */}
          <div className="rounded-lg mb-5 mt-2 pb-8">
            <div className="flex flex-col mt-5 mb-4">
              <div
                className="w-full border border-gray-300 p-1  bg-gray-50"
                id="cardNumber"
              />
            </div>
            <div className="flex flex-row md:flex-row flex-auto">
              <div className="flex flex-col mb-4 sm:mb-0 grow">
                <div
                  className="border border-gray-300 py-1  bg-gray-50 grow"
                  id="expiry"
                />
              </div>
              <div className="flex flex-col ml-3 md:ml-5">
                <div
                  id="cvc"
                  className="border border-gray-300 right-0 py-1  bg-gray-50 grow-0"
                />
              </div>
            </div>
          </div>
        </div>
        <button
          className="p-3 bg-blue-600 text-white rounded inline-block w-full font-semibold text-xs disabled:opacity-50 disabled:bg-blue-600 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          Submit
        </button>
        <div className="text-center text-xs text-gray-500 m-3">
          By submitting your payment, you allow BadenBower to charge your card
          for this subscription, now and every billing date thereafter in
          accordance with our{' '}
          <a href="https://www.badenbower.com/faqs/" target="_blank">
            FAQ
          </a>
          You can always cancel your subscription by emailing prior to your next
          billing date.
        </div>
        <div className="align-baseline md:mt-10 text-gray-400 text-xs decoration-dashed mt-4 text-center md:text-center">
          <span className="text-gray-400 text-xs">
            Powered by <span className="font-bold">AirWallex</span>
          </span>{' '}
          -{' '}
          <a
            href="https://www.badenbower.com/terms-of-use/"
            target={'_blank'}
            className="text-gray-400 text-xs underline"
          >
            Terms and Conditions
          </a>{' '}
          |{' '}
          <a
            href="https://www.badenbower.com/privacy-policy/"
            target={'_blank'}
            className="text-gray-400 text-xs underline"
          >
            Privacy
          </a>
        </div>
      </div>
    </form>
  );
};

export default CustomerDetails;
